import { isShortLink, readPin } from '../data/pins';
import { money, whatsAppNumber } from '../app/words';
import { blank, cell, type SheetRow, type SheetTable } from './file';
import { firstData, type SheetColumns, type SheetField } from './columns';

/**
 * A sheet's rows as stops, read as the phone reads them (lib/services/orders/sheet_reader.dart): a row with an address
 * or a pin is always a stop; whatever else in it can't be read goes on the stop's flags for someone to look at, and
 * stays in its notes when it may matter to the courier. A row with neither is listed with its number and why. Nothing
 * is dropped without a word.
 */

/** A day's stops is far fewer, and Google's lookups are counted per day. */
export const MAX_ROWS = 500;

/** The template's example row (lib/services/orders/sheet_template.dart): left out. */
export const EXAMPLE_ADDRESS = '12 Example Street, Maitama, Abuja';
export const TEMPLATE_URL = 'https://goroutr.com/stops-template.xlsx';

export interface SheetOrder {
  /** The row's number in the sheet. */
  row: number;
  address: string;
  /** The customer's own pin as written (coordinates, a Maps link or a plus code), when there's one. */
  pin: string | null;
  houseNumber: string | null;
  name: string | null;
  phone: string | null;
  access: string | null;
  notes: string[];
  packages: number | null;
  size: 'regular' | 'large' | 'bulky';
  type: 'delivery' | 'pickup';
  /** "HH:MM", and whether it's today (a deadline for another day isn't set, only shown). */
  deliverBy: { time: string; today: boolean; asWritten: string } | null;
  /** What to collect at the door, in minor units (the order, and the delivery fee), when the sheet says. */
  collect: { order: number; fee: number } | null;
  /** A pickup and a delivery with one order number go together. */
  pairKey: string | null;
  /** The courier who takes it, as the sheet names them; and the couriers allowed to, each as named (the web's own columns). */
  courier: string | null;
  allowed: string[];
  /** Those names matched to the business's couriers (couriers.ts), on the web; null: anyone may take it. */
  couriers?: string[] | null;
  flags: string[];
}

export interface SheetRead {
  orders: SheetOrder[];
  /** Rows that aren't stops, and what wasn't read, each saying which row and why. */
  notOrders: string[];
}

export function readRows(t: SheetTable, columns: SheetColumns, o: { now: number; zone: string; currency: string; country: string | null; near?: { lat: number; lng: number } | null }): SheetRead {
  const orders: SheetOrder[] = [];
  const listed: string[] = [];
  let firstOver: number | null = null;
  let lastOver: number | null = null;
  let hidden = 0;
  for (let i = firstData(columns); i < t.rows.length; i++) {
    const row = t.rows[i];
    if (blank(row)) continue;
    if (row.hidden) {
      hidden++;
      continue;
    }
    if (orders.length >= MAX_ROWS) {
      firstOver ??= row.number;
      lastOver = row.number;
      continue;
    }
    const read = readRow(row, columns, o);
    if (read === 'example') listed.push(`Row ${row.number} is the template’s example, so it’s left out.`);
    else if (typeof read === 'string') listed.push(`Row ${row.number}: ${read}`);
    else orders.push(read);
  }
  if (firstOver != null)
    listed.push(firstOver === lastOver ? `Row ${firstOver} wasn’t read: Routr reads ${MAX_ROWS} rows at a time.` : `Rows ${firstOver} to ${lastOver} weren’t read: Routr reads ${MAX_ROWS} rows at a time.`);
  if (hidden) listed.push(hidden === 1 ? '1 row hidden in the sheet is left out.' : `${hidden} rows hidden in the sheet are left out.`);
  if (t.otherSheets.length) {
    const names = t.otherSheets.map((n) => `“${n}”`).join(', ');
    listed.push(t.otherSheets.length === 1 ? `Read the sheet “${t.sheetName}”, not ${names}.` : `Read the sheet “${t.sheetName}”, not the ${t.otherSheets.length} others (${names}).`);
  }
  return { orders, notOrders: listed };
}

function readRow(row: SheetRow, columns: SheetColumns, o: { now: number; zone: string; currency: string; country: string | null; near?: { lat: number; lng: number } | null }): SheetOrder | string | 'example' {
  const cells = new Map<SheetField, string[]>();
  const extra: string[] = [];
  columns.fields.forEach((f, c) => {
    const value = cell(row, c).trim();
    const field = f ?? 'asNote';
    if (!value || field === 'skip') return;
    if (field === 'asNote') extra.push(columns.headerRow == null ? value : `${columns.headers[c]}: ${value}`);
    else cells.set(field, [...(cells.get(field) ?? []), value]);
  });
  const one = (f: SheetField) => cells.get(f)?.[0];

  const parts: string[] = [];
  for (const part of [...(cells.get('address') ?? []), ...(cells.get('area') ?? [])]) if (!parts.join(', ').toLowerCase().includes(part.toLowerCase())) parts.push(part);
  let address = parts.join(', ');
  if (address === EXAMPLE_ADDRESS) return 'example';

  const flags: string[] = [];
  const notes: string[] = [];
  let pin: string | null = null;
  const pinCell = coordinates(one('pin') ?? (one('latitude') && one('longitude') ? `${one('latitude')}, ${one('longitude')}` : undefined));
  if (pinCell != null) {
    if (readPin(pinCell, o.near) || isShortLink(pinCell)) pin = pinCell;
    else if (!address) return `couldn’t read the pin “${pinCell}”, and there’s no address.`;
    else flags.push(`Couldn’t read the pin “${pinCell}”, so the address is used.`);
  }
  if (!address && pin == null) return 'no address or pin.';
  if (!address) address = pin!;

  const order = one('orderNumber');
  if (order) notes.push(/^order\b/i.test(order) ? order : `Order ${order}`);
  notes.push(...(cells.get('notes') ?? []));
  notes.push(...extra);

  const phones = (cells.get('phone') ?? []).flatMap(phonesIn);
  let phone: string | null = null;
  for (const p of phones) {
    const read = readPhone(p, notes, flags, o.country);
    if (read == null) continue;
    if (phone == null) phone = read;
    else if (read !== phone) notes.push(`Also ${read}`);
  }

  let collect: SheetOrder['collect'] = null;
  const totalCell = one('total');
  const amountCell = totalCell ?? one('amount');
  const feeCell = one('fee');
  let fee: number | null = null;
  if (feeCell) {
    if (NOTHING.test(feeCell)) fee = 0;
    else {
      const f = readMoney(feeCell, o.currency, 'fee');
      if (f == null) {
        flags.push(`“${feeCell}” isn’t a delivery fee, so none is set.`);
        notes.push(`Delivery fee: ${feeCell}`);
      } else fee = f.order;
    }
  }
  if (amountCell && !NOTHING.test(amountCell)) {
    if (saysPaid(amountCell)) notes.push(amountCell);
    else {
      const a = readMoney(amountCell, o.currency, 'order');
      if (a == null) {
        flags.push(`“${amountCell}” isn’t an amount, so nothing to collect is set.`);
        notes.push(`Amount: ${amountCell}`);
      } else {
        // A total has the fee in it already; an order amount's fee is on top.
        const f = a.fee ?? fee ?? 0;
        collect = totalCell ? { order: Math.max(0, a.order - (fee ?? 0)), fee: fee ?? 0 } : { order: a.order, fee: f };
      }
    }
  } else if (fee) collect = { order: 0, fee };

  let packages: number | null = null;
  const packagesCell = one('packages');
  if (packagesCell) {
    const n = parseInt(/\d+/.exec(packagesCell)?.[0] ?? '', 10);
    if (!(n >= 1)) flags.push(`“${packagesCell}” isn’t a number of packages, so it’s 1.`);
    else packages = Math.min(99, n);
  }

  let size: SheetOrder['size'] = 'regular';
  const sizeCell = one('size');
  if (sizeCell) {
    const s = SIZES[sizeCell.trim().toLowerCase()];
    if (!s) flags.push(`“${sizeCell}” isn’t Regular, Large or Bulky, so it’s Regular.`);
    else size = s;
  }

  let type: SheetOrder['type'] = 'delivery';
  const typeCell = one('type');
  if (typeCell) {
    const k = typeCell.trim().toLowerCase().replace(/[^a-z]/g, '');
    const s = ['delivery', 'deliver', 'drop', 'dropoff', 'd'].includes(k) ? 'delivery' : ['pickup', 'collect', 'collection', 'p'].includes(k) ? 'pickup' : null;
    if (!s) flags.push(`“${typeCell}” isn’t Pickup or Delivery, so it’s a delivery.`);
    else type = s;
  }

  let deliverBy: SheetOrder['deliverBy'] = null;
  const byCell = one('deliverBy');
  if (byCell) {
    deliverBy = deadlineIn(byCell, o.now, o.zone);
    if (!deliverBy) {
      flags.push(`“${byCell}” isn’t a time, so no deliver-by is set.`);
      notes.push(`Deliver by: ${byCell}`);
    }
  }

  return {
    row: row.number,
    address,
    pin,
    houseNumber: pin === address ? null : (/^(?:plot|no\.?|house|flat|block)?\s*(\d+[a-z]?)\b/i.exec(address)?.[1] ?? null),
    name: cells.get('name')?.join(' ') ?? null,
    phone,
    access: cells.get('landmark')?.join(', ') ?? null,
    notes,
    packages,
    size,
    type,
    deliverBy,
    collect,
    pairKey: order ? `order:${order.toLowerCase()}` : null,
    // A Courier cell naming several ("Tunde, Bayo") means any of them: those allowed.
    courier: namesIn(one('courier') ?? '').length === 1 ? namesIn(one('courier')!)[0] : null,
    allowed: [...(namesIn(one('courier') ?? '').length > 1 ? namesIn(one('courier')!) : []), ...(cells.get('allowed') ?? []).flatMap(namesIn)],
    flags,
  };
}

/** The names in a cell: "Musa, Tunde or Bayo" is three. */
export const namesIn = (c: string) => c.split(/\s*(?:[,/;|&+]|\band\b|\bor\b)\s*/i).map((x) => x.trim()).filter(Boolean);

const SIZES: Record<string, SheetOrder['size']> = {
  regular: 'regular', normal: 'regular', standard: 'regular', small: 'regular', s: 'regular', medium: 'regular', m: 'regular',
  large: 'large', big: 'large', l: 'large',
  bulky: 'bulky', 'extra large': 'bulky', 'very large': 'bulky', xl: 'bulky', heavy: 'bulky',
};

/** In a pin column two numbers can only be coordinates, however few decimals a spreadsheet kept ("9.09, 7.495"). */
export function coordinates(c: string | undefined): string | null {
  if (c == null) return null;
  const m = /^\s*(-?\d{1,2}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/.exec(c);
  if (!m) return c;
  const lat = parseFloat(m[1]);
  const lng = parseFloat(m[2]);
  return Math.abs(lat) > 90 || Math.abs(lng) > 180 ? c : `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

// ── Phones ───────────────────────────────────────────────────────────────

/** The numbers in a phone cell: "0803… / 0812…" is two. */
export const phonesIn = (c: string) => c.split(/\s*(?:[/;,|&]|\bor\b)\s*/i).map((p) => p.trim()).filter(Boolean);

/** A number Excel turned into a rounded figure ("8.03569E+09"): its last digits are gone. */
const ROUNDED = /^\d(?:\.\d+)?e\+?\d+$/i;
/** The number in a cell that has words too: "0803 568 5313 call before 5pm". */
const NUMBER_IN_WORDS = /\+?\d[\d\s().-]{5,}\d/;

/** "+234 803 568 5313" for a Nigerian number; others "+44 7911123456". */
export function phoneText(international: string): string {
  const d = international.replace(/\D/g, '');
  if (d.startsWith('234') && d.length === 13) return `+234 ${d.slice(3, 6)} ${d.slice(6, 9)} ${d.slice(9)}`;
  return `+${d}`;
}

/**
 * The stop's number, as it's typed. Excel takes the 0 off a number it thinks is a figure; those are put right. Words
 * beside one go to the notes for the courier; a number that isn't one is flagged and kept in the notes.
 */
export function readPhone(c: string, notes: string[], flags: string[], country: string | null): string | null {
  if (ROUNDED.test(c)) {
    flags.push(`The spreadsheet rounded the phone number to “${c}”, so it’s left out. Type it again.`);
    return null;
  }
  let phone = c;
  if (/[A-Za-z]/.test(c)) {
    const m = NUMBER_IN_WORDS.exec(c);
    if (m) {
      phone = m[0].trim();
      const words = `${c.slice(0, m.index)} ${c.slice(m.index + m[0].length)}`.replace(/^[\s,:;()\-]+|[\s,:;()\-]+$/g, '');
      if (words) notes.push(words);
    }
  }
  const digits = phone.replace(/\D/g, '');
  const number = whatsAppNumber(phone, country);
  const enough = digits.length >= 7 && digits.length <= 15;
  // Ten digits after Nigeria's code: 0803 568 5313.
  const notNigerian = number != null && number.startsWith('234') && number.length !== 13;
  if (number == null || number.length > 15 || notNigerian) {
    // With no calling code to go by, it's kept as it's written.
    if (whatsAppNumber('0803 000 0000', country) == null && enough) return phone;
    flags.push(`“${phone}” isn’t a phone number, so it’s left out.`);
    notes.push(`Phone: ${phone}`);
    return null;
  }
  return /^[+0][\d\s().-]*$/.test(phone) ? phone : phoneText(`+${number}`);
}

// ── Money ────────────────────────────────────────────────────────────────

const NOTHING = /^(?:[-–—.]+|0+(?:[.,]0+)?|nil|none|n\/?a|free|nothing)$/i;
const PAID = /\b(?:paid|prepaid)\b/i;
const NOT_PAID = /\b(?:not\s+(?:yet\s+)?paid|unpaid)\b/i;
export const saysPaid = (t: string) => PAID.test(t) && !NOT_PAID.test(t);

/** How each currency is written beside an amount (the business's own only: another's isn't read as one). */
const MARKS: Record<string, string[]> = {
  NGN: ['₦', 'ngn', 'naira', 'n'],
  GHS: ['gh₵', '₵', 'ghs', 'cedis', 'ghc'],
  KES: ['ksh', 'kes', 'sh'],
  ZAR: ['r', 'zar'],
  GBP: ['£', 'gbp'],
  USD: ['$', 'usd'],
  EUR: ['€', 'eur'],
};
const ANY_MARK = /[₦₵£$€]|\b(?:ngn|ghs|kes|ksh|zar|gbp|usd|eur|naira|cedis)\b/i;
const NUM = String.raw`(\d{1,3}(?:[,\s]\d{3})+|\d+)(?:\.(\d{1,2}))?\s*(k)?`;

/** One amount in minor units ("₦12,500", "12.5k", "1500.50"), or null when it isn't one in [currency]. */
function amountOf(text: string, currency: string): number | null {
  let t = text.trim().toLowerCase();
  const marks = MARKS[currency] ?? [currency.toLowerCase()];
  for (const mk of [...marks].sort((a, b) => b.length - a.length)) {
    if (t.startsWith(mk) && (mk.length > 1 || /^[^a-z]/.test(mk) || /\d/.test(t[mk.length] ?? ''))) {
      t = t.slice(mk.length).trim();
      break;
    }
    if (t.endsWith(mk) && /[\d\sk]$/.test(t.slice(0, -mk.length))) {
      t = t.slice(0, -mk.length).trim();
      break;
    }
  }
  if (ANY_MARK.test(t)) return null;
  const m = new RegExp(`^${NUM}$`).exec(t);
  if (!m) return null;
  const whole = parseInt(m[1].replace(/[,\s]/g, ''), 10);
  const frac = m[2] ? parseInt(m[2].padEnd(2, '0'), 10) : 0;
  const minor = whole * 100 + frac;
  return m[3] ? Math.round(minor * 1000) : minor;
}

/**
 * An amount cell, as the phone reads one (Money.parse, readAmounts): one amount, or an order and its fee
 * ("5,000 + 1,500 delivery"). Null when it isn't an amount in the business's currency.
 */
export function readMoney(c: string, currency: string, as: 'order' | 'fee'): { order: number; fee: number | null } | null {
  const t = c.trim().replace(/^(?:cod|pay on delivery|delivery fee|fee|total)\s*:?\s*/i, '');
  const one = amountOf(t, currency);
  if (one != null) return { order: one, fee: null };
  if (as === 'order') {
    const plus = /^(.+?)\s*\+\s*(.+?)\s*(?:for\s+)?(?:delivery|dispatch|logistics|shipping)?(?:\s+fee)?$/i.exec(t);
    if (plus) {
      const a = amountOf(plus[1], currency);
      const b = amountOf(plus[2], currency);
      if (a != null && b != null) return { order: a, fee: b };
    }
  }
  return null;
}

/** "COD ₦12,500 · Delivery fee ₦1,500": what to collect, for the notes, when payment at the door is off. */
export const collectWords = (c: { order: number; fee: number }, currency: string) =>
  [c.order ? `COD ${money(c.order, currency)}` : null, c.fee ? `Delivery fee ${money(c.fee, currency)}` : null].filter(Boolean).join(' · ');

// ── Deadlines (RuleReader.deadlineIn and _deadline, rule_reader.dart) ──

const DAYS = 'today|tonight|tomorrow|tmrw|tmr|tomoro|tommorow|tomorow|monday|tuesday|wednesday|thursday|friday|saturday|sunday';
/** "before 2pm", "by 4", "not later than 14:00", "tomorrow by 10", "by noon" (the phone's `_by`). */
const BY = new RegExp(
  `(?:\\b(${DAYS})\\s+)?(?<![A-Za-z])(?:before|by|b4|latest(?:\\s+by)?|not\\s+later\\s+than|no\\s+later\\s+than|on\\s+or\\s+before)\\s+` +
    `(?:((?:12\\s*)?(?:noon|midday))|(\\d{1,2})(?:[:.](\\d{2}))?\\s*([ap]\\.\\s?m\\.?|[ap]\\s?m|noon|o['’]?clock)?)` +
    `(?![\\d:])(?!\\s*(?:st|nd|rd|th)\\b)(?![A-Za-z])(?:\\s+(${DAYS})\\b)?`,
  'i',
);
const ANY_DAY = new RegExp(`\\b(?:${DAYS}|next\\s+week)\\b`, 'i');
/** A date in a cell: "2026-10-30", "30/10/2026", "30-10-26" (day first, as Nigeria writes them). */
const CELL_DATE = /\b(?:(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})|(\d{1,2})[-/.](\d{1,2})[-/.](\d{4}|\d{2}))\b/;

/** [t]'s day in [zone]: year, month, day, weekday and the time of day in minutes. */
function zoneDay(t: number, zone: string) {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'long', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' })
      .formatToParts(t)
      .map((x) => [x.type, x.value]),
  );
  return { y: +p.year, m: +p.month, d: +p.day, weekday: String(p.weekday).toLowerCase(), minutes: +p.hour * 60 + +p.minute };
}

/**
 * A deadline written in a cell of its own, read as the phone reads one: "14:00", "2pm", "5" or "by 5" (an hour with no am
 * or pm is the reading still ahead of now, the earlier if both are), "by noon", "tomorrow 10am" (and its misspellings),
 * with a date or not. A date that isn't today's, or another day's name, is another day. Null when it gives no time.
 */
export function deadlineIn(value: string, now: number, zone: string): { time: string; today: boolean; asWritten: string } | null {
  let text = value.trim();
  const today = zoneDay(now, zone);
  let other = false;
  const date = CELL_DATE.exec(text);
  if (date) {
    const [y, m, d] = date[1] ? [+date[1], +date[2], +date[3]] : [+date[6] < 100 ? 2000 + +date[6] : +date[6], +date[5], +date[4]];
    other = !(y === today.y && m === today.m && d === today.d);
    text = `${text.slice(0, date.index)} ${text.slice(date.index + date[0].length)}`.trim();
  }
  const day = ANY_DAY.exec(text)?.[0];
  if (day) text = text.replace(day, ' ').trim();
  // Excel's times come with seconds: "14:30:00".
  text = text.replace(/\b(\d{1,2}:\d{2}):\d{2}\b/, '$1');
  const m = BY.exec(`${day ?? ''} by ${text}`);
  if (!m) return null;
  let h: number;
  let min = 0;
  const ap = m[6]?.toLowerCase().replace(/[^a-z]/g, '');
  if (m[2] != null || ap === 'noon') h = 12;
  else {
    h = +m[3];
    min = m[4] ? +m[4] : 0;
    if (min > 59) return null;
    if (ap === 'pm' || ap === 'am') {
      if (h < 1 || h > 12) return null;
      h = (h % 12) + (ap === 'pm' ? 12 : 0);
    } else if (h > 23) return null;
    else if (h >= 1 && h <= 11) {
      const ahead = [h, h + 12].filter((c) => c * 60 + min > today.minutes);
      h = ahead.length ? ahead[0] : h + 12;
    }
  }
  const named = (m[1] ?? m[7] ?? day)?.toLowerCase();
  const isToday = named == null || named === 'today' || named === 'tonight' || named === today.weekday;
  return { time: `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`, today: isToday && !other, asWritten: value.trim() };
}
